import { useRef, useCallback, useEffect } from 'react';
import {
  TRIGGER_BRACES,
  allSuggestionState,
  caretInsideCompleteReference,
  resolveDynSuggestionState,
} from './syntaxHighlightHelpers';

/** Opening the dynamic-variable suggestion list. */
export function useDynamicVarPicker({
  showDynamicVarPicker,
  suggest,
  suggestTrigger,
  setDynSuggestions,
  editableRef,
  positionDynSuggestions,
  getPlainText,
  getSelectionOffsets,
  suggestionScopes,
}) {
  /**
   * One pending open at a time.
   *
   * A single click schedules this from BOTH `handleMouseDown` and
   * `handleFocus`, each on its own `setTimeout(…, 0)`. Two attempts race over
   * the same dropdown, and the second one re-reads the field: if that read
   * lands between the highlight re-render and the caret being restored, it sees
   * a state the first did not and takes the `else` branch, which CLOSES what
   * the first just opened. The result is a list that appears and vanishes
   * (owner, 22.09.2026 — reported in the Environments table).
   *
   * Cancelling the previous timer makes the two handlers idempotent: whichever
   * fires last is the one that decides, and it decides once.
   */
  const pickerTimerRef = useRef(null);
  const openDynamicVarPickerRef = useRef(null);
  const scheduleDynamicVarPicker = useCallback(() => {
    if (pickerTimerRef.current !== null) clearTimeout(pickerTimerRef.current);
    pickerTimerRef.current = setTimeout(() => {
      pickerTimerRef.current = null;
      // Every caller of this is a click or the focus that follows one.
      openDynamicVarPickerRef.current?.({ source: 'pointer' });
    }, 0);
  }, []);

  useEffect(() => () => {
    if (pickerTimerRef.current !== null) clearTimeout(pickerTimerRef.current);
  }, []);

  const openDynamicVarPicker = useCallback((options = {}) => {
    if (!showDynamicVarPicker || !editableRef.current) return;
    const plainText = getPlainText(editableRef.current);
    const offsets = getSelectionOffsets();
    const cursorPos = offsets?.start ?? plainText.length;
    // Clicking into a finished `{{name}}` says nothing the hover tooltip has
    // not already said, and the editor popup is what that click is FOR.
    if (options.source === 'pointer' && caretInsideCompleteReference(plainText, cursorPos)) {
      setDynSuggestions(null);
      return;
    }
    const textBefore = plainText.substring(0, cursorPos);
    const scopes = suggestionScopes();
    const partial = resolveDynSuggestionState(textBefore, cursorPos, scopes, suggest, suggestTrigger);
    if (partial) {
      positionDynSuggestions(partial);
    } else if (plainText === '' && suggestTrigger !== TRIGGER_BRACES) {
      // An EMPTY field offers everything. That covers three moments with one
      // rule: clicking into it, and deleting back to empty — which must bring
      // the whole list back rather than leaving it hidden because the last
      // thing typed matched nothing (owner, 23.09.2026) — and any later call
      // in the same gesture, which is what used to close a list the click had
      // just opened.
      //
      // A field that HAS content and matches nothing falls through and closes,
      // which is what keeps clicking into a filled value quiet.
      positionDynSuggestions(allSuggestionState(cursorPos, scopes, suggest));
    } else {
      setDynSuggestions(null);
    }
  }, [showDynamicVarPicker, getPlainText, getSelectionOffsets, positionDynSuggestions, suggestionScopes, suggest, suggestTrigger, editableRef, setDynSuggestions]);
  openDynamicVarPickerRef.current = openDynamicVarPicker;

  return { scheduleDynamicVarPicker, openDynamicVarPicker };
}
